import { useMemo } from 'react'
import { Link } from 'react-router-dom'

// Lightweight Lean 4 tokenizer for inline snippets. NOT a real parser —
// just enough to colorize keywords, types, comments, strings, and
// numbers. Monaco is reserved for the full-file slide-over.

const KEYWORDS = new Set([
  'theorem', 'lemma', 'def', 'abbrev', 'example', 'axiom', 'structure',
  'inductive', 'class', 'instance', 'namespace', 'end', 'open', 'import',
  'variable', 'variables', 'section', 'where', 'with', 'in', 'let',
  'have', 'show', 'fun', 'if', 'then', 'else', 'match', 'do', 'return',
  'by', 'apply', 'exact', 'intro', 'intros', 'rfl', 'simp', 'rw',
  'decide', 'native_decide', 'sorry', 'admit', 'constructor', 'cases',
  'induction', 'noncomputable', 'protected', 'private', 'deriving',
])

const TYPES = new Set([
  'Nat', 'Int', 'Bool', 'String', 'List', 'Fin', 'Prop', 'Type', 'Sort',
  'Option', 'Real', 'Matrix', 'Set', 'Architecture', 'Schedule', 'Zone',
  'Channel', 'SysCall', 'SysCallKind', 'LogicalSchedule', 'LogicalGate',
  'LogicalImpl', 'PhysicalImpl', 'Statement', 'ImplementerSubmission',
  'QECCode', 'CodeBlockBinding', 'LogicalQubitBinding', 'CodedLogicalLayout',
  'LogicalLayout', 'ZoneRole', 'ChannelKind', 'InitialPlacement',
  'PPMGadget', 'LogicalGateSet', 'PauliMeasurementClaim',
])

type Token = { text: string; kind: 'plain' | 'kw' | 'type' | 'comment' | 'string' | 'num' | 'op' | 'punct' | 'attr' | 'ident-decl' }

function tokenizeLean(src: string): Token[] {
  const out: Token[] = []
  let i = 0
  const n = src.length
  while (i < n) {
    const c = src[i]
    // Block comment /- ... -/
    if (c === '/' && src[i + 1] === '-') {
      let j = i + 2
      while (j < n && !(src[j] === '-' && src[j + 1] === '/')) j++
      out.push({ text: src.slice(i, Math.min(j + 2, n)), kind: 'comment' })
      i = Math.min(j + 2, n)
      continue
    }
    // Line comment -- ...
    if (c === '-' && src[i + 1] === '-') {
      let j = i
      while (j < n && src[j] !== '\n') j++
      out.push({ text: src.slice(i, j), kind: 'comment' })
      i = j
      continue
    }
    // String literal
    if (c === '"') {
      let j = i + 1
      while (j < n && src[j] !== '"') {
        if (src[j] === '\\') j++
        j++
      }
      out.push({ text: src.slice(i, Math.min(j + 1, n)), kind: 'string' })
      i = Math.min(j + 1, n)
      continue
    }
    // Number
    if (/[0-9]/.test(c)) {
      let j = i
      while (j < n && /[0-9_eE.]/.test(src[j])) j++
      out.push({ text: src.slice(i, j), kind: 'num' })
      i = j
      continue
    }
    // Identifier / keyword / type
    if (/[A-Za-z_]/.test(c)) {
      let j = i
      while (j < n && /[A-Za-z0-9_'.]/.test(src[j])) j++
      const word = src.slice(i, j)
      // Look back: is this preceded by 'def'/'theorem'/'lemma' (after whitespace)?
      const prevText = out.length > 0 ? out[out.length - 1].text : ''
      const prevKind = out.length > 0 ? out[out.length - 1].kind : null
      const prevPrevKind = out.length > 1 ? out[out.length - 2].kind : null
      const isDeclName =
        (prevKind === 'kw' && (prevText === 'def' || prevText === 'theorem' || prevText === 'lemma' || prevText === 'abbrev' || prevText === 'example' || prevText === 'structure' || prevText === 'inductive' || prevText === 'axiom')) ||
        // also handle whitespace between keyword and name
        (prevKind === 'plain' && /^\s+$/.test(prevText) && prevPrevKind === 'kw')

      if (KEYWORDS.has(word)) out.push({ text: word, kind: 'kw' })
      else if (TYPES.has(word) || /^[A-Z]/.test(word)) out.push({ text: word, kind: 'type' })
      else if (isDeclName) out.push({ text: word, kind: 'ident-decl' })
      else out.push({ text: word, kind: 'plain' })
      i = j
      continue
    }
    // Operators
    if (/[=:→⇒↦∀∃∧∨¬≤≥≠<>+\-*\/^%]/.test(c)) {
      let j = i
      while (j < n && /[=:→⇒↦∀∃∧∨¬≤≥≠<>+\-*\/^%]/.test(src[j])) j++
      out.push({ text: src.slice(i, j), kind: 'op' })
      i = j
      continue
    }
    // Punctuation
    if (/[(){}\[\],;.|_]/.test(c)) {
      out.push({ text: c, kind: 'punct' })
      i++
      continue
    }
    // Whitespace + everything else: lump
    let j = i
    while (j < n && /[^A-Za-z_0-9\/"=:→⇒↦∀∃∧∨¬≤≥≠<>+\-*\^%(){}\[\],;.|]/.test(src[j])) j++
    out.push({ text: src.slice(i, Math.max(j, i + 1)), kind: 'plain' })
    i = Math.max(j, i + 1)
  }
  return out
}

const COLOR: Record<Token['kind'], string> = {
  plain:        'text-slate-100',
  kw:           'text-violet-300 font-semibold',
  type:         'text-cyan-200',
  comment:      'text-emerald-400/80 italic',
  string:       'text-amber-200',
  num:          'text-rose-300',
  op:           'text-pink-300',
  punct:        'text-slate-400',
  attr:         'text-yellow-200',
  'ident-decl': 'text-yellow-200 font-semibold',
}

interface LeanSnippetProps {
  code: string
  filePath?: string
  startLine?: number
  caption?: string
  maxHeight?: string
  fullFileLink?: { path: string; line?: number; onClick?: (p: string, l?: number) => void }
}

function LeanSnippet({ code, filePath, startLine, caption, maxHeight = '320px', fullFileLink }: LeanSnippetProps) {
  const tokens = useMemo(() => tokenizeLean(code), [code])
  return (
    <div className="rounded-lg overflow-hidden border border-slate-800 bg-slate-900 shadow-sm">
      {(filePath || caption) && (
        <div className="flex items-center justify-between px-3 py-1.5 bg-slate-950/60 border-b border-slate-800 text-[11px]">
          <div className="flex items-baseline gap-2 min-w-0 flex-1">
            {caption && <span className="text-slate-200 font-semibold truncate">{caption}</span>}
            {filePath && (
              <span className="font-mono text-slate-500 truncate" title={filePath}>
                {filePath}{startLine != null && `:${startLine}`}
              </span>
            )}
          </div>
          {fullFileLink && (
            fullFileLink.onClick ? (
              <button
                onClick={() => fullFileLink.onClick!(fullFileLink.path, fullFileLink.line)}
                className="text-[10.5px] text-indigo-300 hover:text-indigo-200 font-medium ml-2 flex-shrink-0"
              >
                Open full file →
              </button>
            ) : (
              <Link
                to={`/resource-verification?file=${encodeURIComponent(fullFileLink.path)}`}
                className="text-[10.5px] text-indigo-300 hover:text-indigo-200 font-medium ml-2 flex-shrink-0"
              >
                Open full file →
              </Link>
            )
          )}
        </div>
      )}
      <pre className="text-[12px] font-mono leading-relaxed p-3 overflow-auto" style={{ maxHeight }}>
        <code>
          {tokens.map((t, i) => (
            <span key={i} className={COLOR[t.kind]}>{t.text}</span>
          ))}
        </code>
      </pre>
    </div>
  )
}

export default LeanSnippet
